import React, { useState, useEffect, useMemo } from "react";
import "./ItemModal.css";
import { useCart } from "../context/CartContext";
import ErrorModal from "./ErrorModal";
import { useScrollLock } from "../hooks/useScrollLock";

const ItemModal = ({ isOpen, item, onClose, showToast, mode, cartItem }) => {
  const [selectedRequiredOption, setSelectedRequiredOption] = useState(
    mode === "edit" && cartItem?.selectedRequiredOption
      ? cartItem.selectedRequiredOption
      : null
  );

  const [selectedOptionalOptions, setSelectedOptionalOptions] = useState(
    mode === "edit" && cartItem?.selectedOptionalOptions
      ? [
          ...cartItem.selectedOptionalOptions,
          ...(cartItem.item?.swallowOptions || []),
        ]
      : []
  );

  const [selectedProteins, setSelectedProteins] = useState(
    mode === "edit" && cartItem?.item?.proteins ? cartItem.item.proteins : []
  );

  const [quantity, setQuantity] = useState(
    mode === "edit" && cartItem?.quantity ? cartItem.quantity : 1
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [showSwallowOptions, setShowSwallowOptions] = useState(false);
  const [showProteins, setShowProteins] = useState(false);
  const [showExtraProteins, setShowExtraProteins] = useState(false);

  const { addToCart, updateCartItem } = useCart();

  // Constants
  const swallowOptionIds = useMemo(() => [6, 7, 8, 9, 92, 93, 98], []);
  const noSwallowId = 98;

  const mainProteinIds = [104, 105, 106, 107, 108];

  useScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen || !item) return;

    const swallowOptions = item.optional_options.filter((opt) =>
      swallowOptionIds.includes(Number(opt.id))
    );

    const proteinOptions = item.optional_options.filter(
      (opt) => !swallowOptionIds.includes(Number(opt.id))
    );

    setShowSwallowOptions((prev) =>
      prev === false && swallowOptions.length > 0 ? true : prev
    );

    setShowProteins((prev) =>
      prev === false && swallowOptions.length === 0 && proteinOptions.length > 0
        ? true
        : prev
    );
  }, [isOpen, item, swallowOptionIds]);

  // ------------------- Handlers -------------------
  const handleRequiredChange = (option) => setSelectedRequiredOption(option);

  const handleOptionalChange = (option) => {
    const exists = selectedOptionalOptions.find((opt) => opt.id === option.id);
    if (exists) {
      setSelectedOptionalOptions((prev) =>
        prev.filter((opt) => opt.id !== option.id)
      );
    } else {
      setSelectedOptionalOptions([
        ...selectedOptionalOptions,
        { ...option, quantity: 1 },
      ]);
    }
  };

  const updateOptionalQuantity = (optionId, change) => {
    setSelectedOptionalOptions((prev) =>
      prev.map((opt) =>
        Number(opt.id) === Number(optionId)
          ? { ...opt, quantity: Math.max(1, opt.quantity + change) }
          : opt
      )
    );
  };

  const handleProteinChange = (option) => {
    const exists = selectedProteins.find((opt) => opt.id === option.id);

    if (exists) {
      setSelectedProteins((prev) => prev.filter((opt) => opt.id !== option.id));
    } else {
      setSelectedProteins((prev) => [...prev, { ...option, quantity: 1 }]);
    }
  };

  const updateProteinQuantity = (optionId, change) => {
    setSelectedProteins((prev) =>
      prev.map((opt) =>
        Number(opt.id) === Number(optionId)
          ? { ...opt, quantity: Math.max(1, opt.quantity + change) }
          : opt
      )
    );
  };

  const increaseQuantity = () => setQuantity(quantity + 1);
  const decreaseQuantity = () => quantity > 1 && setQuantity(quantity - 1);

  // ------------------- Option Lists -------------------
  const swallowOptions = item.optional_options.filter((opt) =>
    swallowOptionIds.includes(Number(opt.id))
  );

  const sortedSwallowOptions = [...swallowOptions].sort((a, b) => {
    if (Number(a.id) === noSwallowId) return 1;
    if (Number(b.id) === noSwallowId) return -1;
    return 0;
  });

  const proteinOptions = item.optional_options.filter(
    (opt) => !swallowOptionIds.includes(Number(opt.id))
  );
  const mainProteins = proteinOptions.filter((opt) =>
    mainProteinIds.includes(Number(opt.id))
  );

  const extraProteins = proteinOptions.filter(
    (opt) => !mainProteinIds.includes(Number(opt.id))
  );

  const isNoSwallowSelected = selectedOptionalOptions.some(
    (opt) => Number(opt.id) === noSwallowId
  );

  const isOutOfStock = Number(item.in_stock) === 0;

  const requiredOptions = item.required_options || [];

  // ------------------- Add To Cart -------------------
  const handleAddToCart = () => {
    // ------------------- VALIDATION -------------------
    if ((item.required_options?.length || 0) > 0 && !selectedRequiredOption) {
      setErrorMessage("Please select a required option.");
      return;
    }

    if (swallowOptions.length > 0 && selectedOptionalOptions.length === 0) {
      setErrorMessage("Please select a swallow option or choose No Swallow.");
      return;
    }

    const totalMainProteinQty = selectedProteins
      .filter((opt) => mainProteinIds.includes(Number(opt.id)))
      .reduce((sum, opt) => sum + (opt.quantity || 0), 0);

    if (mainProteins.length > 0 && totalMainProteinQty < 2) {
      setErrorMessage("Please select 2 protein options.");
      return;
    }

    // ------------------- MAP OPTIONS -------------------
    const mapOptions = (opts = []) =>
      opts.map((opt) => ({
        id: opt.id,
        option_name: opt.option_name,
        option_price: Number(opt.option_price || 0),
        quantity: Number(opt.quantity || 1),
      }));

    // separate main and extra
    const mainProteinsSelected = selectedProteins.filter((opt) =>
      mainProteinIds.includes(Number(opt.id))
    );
    const extraProteinsSelected = selectedProteins.filter(
      (opt) => !mainProteinIds.includes(Number(opt.id))
    );

    const newItem = {
      item: {
        ...item,
        proteins: [...mainProteinsSelected, ...extraProteinsSelected],
        swallowOptions: mapOptions(
          selectedOptionalOptions.filter((opt) =>
            swallowOptionIds.includes(Number(opt.id))
          )
        ),
      },
      quantity,
      selectedRequiredOption: selectedRequiredOption
        ? {
            id: selectedRequiredOption.id,
            name: selectedRequiredOption.option_name,
            price: Number(selectedRequiredOption.option_price || 0),
          }
        : null,
      selectedOptionalOptions: mapOptions(
        selectedOptionalOptions.filter(
          (opt) => !swallowOptionIds.includes(Number(opt.id))
        )
      ),
    };

    // ------------------- ADD OR EDIT -------------------
    if (mode === "edit" && cartItem) {
      // Editing existing cart item
      newItem.key = cartItem.key; // preserve key
      updateCartItem(cartItem.key, newItem); // update item in cart
      showToast && showToast("Item successfully updated");
    } else {
      // Adding new item
      newItem.key = `${item.id}-${Date.now()}-${Math.random()}`;
      addToCart(newItem);
      showToast && showToast("Item successfully added to basket");
    }

    // ------------------- CLOSE MODAL -------------------
    onClose();
  };

  const getOptionDisplayPrice = (option, selected) => {
    const qty = selected ? selected.quantity : 1;
    return (parseFloat(option.option_price || 0) * qty).toFixed(2);
  };

  const totalPrice = useMemo(() => {
    let total = parseFloat(item.price) || 0;

    if (selectedRequiredOption)
      total += parseFloat(selectedRequiredOption.option_price || 0);

    selectedOptionalOptions.forEach((opt) => {
      total += parseFloat(opt.option_price || 0) * opt.quantity;
    });

    selectedProteins.forEach((opt) => {
      total += parseFloat(opt.option_price || 0) * opt.quantity;
    });

    return total * quantity;
  }, [
    item.price,
    selectedRequiredOption,
    selectedOptionalOptions,
    selectedProteins,
    quantity,
  ]);

  const totalMainProteinQty = selectedProteins
    .filter((opt) => mainProteinIds.includes(Number(opt.id)))
    .reduce((sum, opt) => sum + (opt.quantity || 0), 0);

  if (!isOpen || !item) return null;

  // ------------------- Render -------------------
  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-top">
            <h2>{item.item_name}</h2>
            <i className="fas fa-times" onClick={onClose}></i>
          </div>

          <div className="modal-body">
            <img
              src={
                item.image
                  ? `https://lasgidirestaurant.com/${item.image}`
                  : "/placeholder.png"
              }
              className="modal-item-image"
              alt={item.item_name}
            />
            <p className="modal-item-desc">{item.item_description}</p>

            {/* Required Options */}
            {requiredOptions.length > 0 && (
              <div className="modal-section">
                <div className="modal-section-heading">
                  <h4>Required Options</h4>
                </div>
                {requiredOptions.map((option) => (
                  <label key={option.id} className="option-label-inline">
                    <div className="option-info">
                      <input
                        type="radio"
                        name="required-option"
                        value={option.id}
                        checked={selectedRequiredOption?.id === option.id}
                        onChange={() => handleRequiredChange(option)}
                      />
                      <span>
                        {option.option_name} (£
                        {parseFloat(option.option_price).toFixed(2)})
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {/* Swallow Options */}
            {swallowOptions.length > 0 && (
              <div className="modal-section">
                <div
                  className="modal-section-heading collapsible-heading"
                  onClick={() => {
                    setShowSwallowOptions(!showSwallowOptions);
                    if (!showSwallowOptions) {
                      setShowProteins(false);
                      setShowExtraProteins(false);
                    }
                  }}
                >
                  <h4>Swallow Options</h4>
                  <span
                    className={`toggle-icon ${
                      showSwallowOptions ? "rotate" : ""
                    }`}
                  >
                    &#9654;
                  </span>
                </div>

                {showSwallowOptions &&
                  sortedSwallowOptions.map((option) => {
                    const selected = selectedOptionalOptions.find(
                      (opt) => Number(opt.id) === Number(option.id)
                    );
                    const qty = selected ? selected.quantity : 1;
                    const isDisabled =
                      isNoSwallowSelected && Number(option.id) !== noSwallowId;

                    return (
                      <label key={option.id} className="option-label-inline">
                        <div className="option-info">
                          <input
                            type="checkbox"
                            checked={!!selected}
                            onChange={() => handleOptionalChange(option)}
                            disabled={isDisabled}
                          />
                          <span>
                            {option.option_name} (£
                            {getOptionDisplayPrice(option, selected)})
                          </span>
                        </div>

                        {option.allow_quantity && selected && (
                          <div className="quantity-control-inline">
                            <button
                              onClick={() =>
                                updateOptionalQuantity(option.id, -1)
                              }
                              disabled={qty <= 1}
                            >
                              -
                            </button>
                            <span className="quantity-value-inline">{qty}</span>
                            <button
                              onClick={() =>
                                updateOptionalQuantity(option.id, 1)
                              }
                            >
                              +
                            </button>
                          </div>
                        )}
                      </label>
                    );
                  })}
              </div>
            )}

            {/* Main Protein Options */}
            {mainProteins.length > 0 && (
              <div className="modal-section">
                <div
                  className="modal-section-heading collapsible-heading"
                  onClick={() => {
                    setShowProteins(!showProteins);
                    if (!showProteins) {
                      setShowSwallowOptions(false);
                      setShowExtraProteins(false);
                    }
                  }}
                >
                  <h4>Protein Options</h4>
                  <span
                    className={`toggle-icon ${showProteins ? "rotate" : ""}`}
                  >
                    &#9654;
                  </span>
                </div>

                {showProteins &&
                  mainProteins.map((option) => {
                    const selected = selectedProteins.find(
                      (opt) => opt.id === option.id
                    );
                    const qty = selected ? selected.quantity : 1;
                    const isDisabled =
                      totalMainProteinQty >= 2 &&
                      !selectedProteins.find((opt) => opt.id === option.id) &&
                      mainProteinIds.includes(Number(option.id)); // Max 2 quantity disables unselected

                    return (
                      <label key={option.id} className="option-label-inline">
                        <div className="option-info">
                          <input
                            type="checkbox"
                            checked={!!selected}
                            onChange={() => handleProteinChange(option)}
                            disabled={isDisabled}
                          />
                          <span>
                            {option.option_name} (£
                            {parseFloat(option.option_price).toFixed(2)})
                          </span>
                        </div>

                        {selected && option.allow_quantity && (
                          <div className="quantity-control-inline">
                            <button
                              onClick={() =>
                                updateProteinQuantity(option.id, -1)
                              }
                              disabled={qty <= 1}
                            >
                              -
                            </button>
                            <span className="quantity-value-inline">{qty}</span>
                            <button
                              onClick={() =>
                                updateProteinQuantity(option.id, 1)
                              }
                              disabled={qty >= 2 || totalMainProteinQty >= 2}
                            >
                              +
                            </button>
                          </div>
                        )}
                      </label>
                    );
                  })}
              </div>
            )}

            {/* Extra Protein Options */}
            {extraProteins.length > 0 && (
              <div className="modal-section">
                <div
                  className="modal-section-heading collapsible-heading"
                  onClick={() => {
                    setShowExtraProteins(!showExtraProteins);
                    if (!showExtraProteins) {
                      setShowSwallowOptions(false);
                      setShowProteins(false);
                    }
                  }}
                >
                  <h4>Extra</h4>
                  <span
                    className={`toggle-icon ${
                      showExtraProteins ? "rotate" : ""
                    }`}
                  >
                    &#9654;
                  </span>
                </div>

                {showExtraProteins &&
                  extraProteins.map((option) => {
                    const selected = selectedProteins.find(
                      (opt) => opt.id === option.id
                    );
                    const qty = selected ? selected.quantity : 1;

                    return (
                      <label key={option.id} className="option-label-inline">
                        <div className="option-info">
                          <input
                            type="checkbox"
                            checked={!!selected}
                            onChange={() => handleProteinChange(option)}
                          />
                          <span>
                            {option.option_name} (£
                            {(parseFloat(option.option_price) * qty).toFixed(2)}
                            )
                          </span>
                        </div>

                        {selected && option.allow_quantity && (
                          <div className="quantity-control-inline">
                            <button
                              onClick={() =>
                                updateProteinQuantity(option.id, -1)
                              }
                              disabled={qty <= 1}
                            >
                              -
                            </button>
                            <span className="quantity-value-inline">{qty}</span>
                            <button
                              onClick={() =>
                                updateProteinQuantity(option.id, 1)
                              }
                            >
                              +
                            </button>
                          </div>
                        )}
                      </label>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Modal Bottom */}
          <div className="modal-bottom">
            <div className="quantity-section">
              <button
                onClick={decreaseQuantity}
                className="quantity-btn"
                disabled={isOutOfStock}
              >
                -
              </button>
              <span
                className={`quantity-value ${isOutOfStock ? "disabled" : ""}`}
              >
                {quantity}
              </span>
              <button
                onClick={increaseQuantity}
                className="quantity-btn"
                disabled={isOutOfStock}
              >
                +
              </button>
            </div>

            <button
              className="add-to-cart-button"
              onClick={handleAddToCart}
              disabled={isOutOfStock}
            >
              <div className="add-to-cart-button-text">
                <span>Add to Basket</span>
                <span>£{totalPrice.toFixed(2)}</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {errorMessage && (
        <ErrorModal
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}
    </>
  );
};

export default ItemModal;
